import React from 'react';
import { CheckCircle2, PlusCircle, XCircle } from 'lucide-react';
import styles from './PricingSection.module.css';
import { Link } from 'react-router-dom';

const PricingSection = () => {
  const renderFeatures = (planType) => {
    const features = {
      free: {
        title: 'Basic',
        price: '0',
        yearlyPrice: '0',
        annualCost: '0',
        features: [
          { text: 'Practice up to: 1 min every 24 hours', included: true },
          { text: 'Real-time video analysis + full transcript', included: true, highlight: 'Video transcript at completion' },
          { text: 'Advanced Analytics', included: true },
        ]
      },
      student: {
        title: 'Student',
        price: '3',
        // Update: Now we show 11 months cost instead of monthly with discount
        yearlyTotal: '33', // 11 months × $3/month
        features: [
          { text: 'Practice up to: 30 mins every 24 hours', included: true },
          { text: 'Real-time video analysis (no transcription)', included: true, highlight: 'No transcript at completion' },
          { text: 'Advanced Analytics', included: false },
          { text: 'No student verification needed', included: true },
        ]
      },
      pro: {
        title: 'Professional',
        price: '10',
        // Update: Now we show 11 months cost instead of monthly with discount
        yearlyTotal: '110', // 11 months × $10/month
        features: [
          { text: 'Practice up to: 30 mins every 24 hours', included: true },
          { text: 'Real-time video analysis + full transcript', included: true, highlight: 'Video transcript at completion' },
          { text: 'Advanced Analytics', included: true },
        ],
        analytics: [
          'Filler Word Detection',
          'Word Clarity Score',
          'Pause Analysis',
          'Pitch Range Over Time',
          'Follow-up Questions',
          'Comprehensive Summary',
          'Visual Performance Graphs',
        ]
      }
    };

    return features[planType];
  }

  return (
    <section id="pricing" className={styles.pricingSection}>
      <h2>Plans & Pricing</h2>
      <p className={styles.subtitle}>Choose the plan that best fits your presentation needs</p>

      <div className={styles.pricingCards}>
        {/* Basic Plan */}
        <div className={styles.pricingCard}>
          <h3>{renderFeatures('free').title}</h3>
          <div className={styles.priceContainer}>
            <div className={styles.price}>
              ${renderFeatures('free').price}
              <span>/month</span>
            </div>
            <div className={styles.yearlyPrice}>
              Free forever
            </div>
          </div>
          <ul className={styles.featuresList}>
            {renderFeatures('free').features.map((feature, index) => (
              <li key={index} className={feature.included ? '' : styles.notIncluded}>
                {feature.included ? (
                  <CheckCircle2 size={16} className={styles.checkIcon} />
                ) : (
                  <XCircle size={16} className={styles.xIcon} />
                )}
                <span>
                  {feature.text}
                  {feature.highlight && <div className={styles.featureHighlight}>{feature.highlight}</div>}
                </span>
              </li>
            ))}
          </ul>
          <Link to="https://app.topspeaks/login" className={styles.priceButton}>
            Get Started
          </Link>
        </div>

        {/* Professional Plan */}
        <div className={`${styles.pricingCard} ${styles.featured}`}>
          <div className={styles.popularBadge}>Best Choice</div>
          <h3>{renderFeatures('pro').title}</h3>
          <div className={styles.priceContainer}>
            <div className={styles.price}>
              ${renderFeatures('pro').price}
              <span>/month</span>
            </div>
            <div className={styles.yearlyPrice}>
              ${renderFeatures('pro').yearlyTotal}/year with annual billing
              <span className={styles.savingsBadge}>1 month free</span>
            </div>
          </div>
          <ul className={styles.featuresList}>
            {renderFeatures('pro').features.map((feature, index) => (
              <li key={index} className={feature.included ? '' : styles.notIncluded}>
                {feature.included ? (
                  <CheckCircle2 size={16} className={styles.checkIcon} />
                ) : (
                  <XCircle size={16} className={styles.xIcon} />
                )}
                <span>
                  {feature.text}
                  {feature.highlight && <div className={styles.featureHighlight}>{feature.highlight}</div>}
                </span>
              </li>
            ))}
          </ul>
          {/* Analytics panel for Pro plan */}
          <div className={styles.analyticsPanel}>
            <h4>Advanced Analytics Included:</h4>
            <div className={styles.analyticsGrid}>
              {renderFeatures('pro').analytics.map((item, index) => (
                <div key={index} className={styles.analyticsItem}>
                  <CheckCircle2 size={14} className={styles.analyticsIcon} />
                  {item}
                </div>
              ))}
            </div>
          </div>
          <button className={styles.priceButton}>
            Coming Soon
          </button>
        </div>

        {/* Student Plan */}
        <div className={`${styles.pricingCard}`}>
          <h3>{renderFeatures('student').title}</h3>
          <div className={styles.priceContainer}>
            <div className={styles.price}>
              ${renderFeatures('student').price}
              <span>/month</span>
            </div>
            <div className={styles.yearlyPrice}>
              ${renderFeatures('student').yearlyTotal}/year with annual billing
              <span className={styles.savingsBadge}>1 month free</span>
            </div>
          </div>
          <ul className={styles.featuresList}>
            {renderFeatures('student').features.map((feature, index) => (
              <li key={index} className={feature.included ? '' : styles.notIncluded}>
                {feature.included ? (
                  <CheckCircle2 size={16} className={styles.checkIcon} />
                ) : (
                  <XCircle size={16} className={styles.xIcon} />
                )}
                <span>
                  {feature.text}
                  {feature.highlight && <div className={styles.featureHighlight}>{feature.highlight}</div>}
                </span>
              </li>
            ))}
          </ul>
          <button className={styles.priceButton}>
            Coming Soon
          </button>
        </div>
      </div>
      <div className={styles.additionalMinutesSection}>
        <h3>Need More Practice Time?</h3>
        <div className={styles.additionalMinutesContent}>
          <PlusCircle size={32} className={styles.plusIcon} />
          <p>
            You can purchase additional minutes for practice.
          </p>
          <div className={styles.minutesBox}>
            <div className={styles.minutesRate}>
              <span className={styles.minutesValue}>1 USD = 30 minutes</span>
              <span className={styles.minutesLimit}>(Max 600 minutes)</span>
            </div>
          </div>
          <p className={styles.purchaseNote}>
            Top up practice minutes anytime directly from your account dashboard.
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;